import type { ReactNode } from 'react';
import {
  entityArrayLength,
  entityName,
  entityNumber,
  entityString,
  formatDate,
  formatMoney,
  requestBranchId,
  type ChangeRequest,
  type ModerationEntity,
  type Translate,
} from '@/entities/moderation';
import { useBranchName } from '../../../model/use-branch-name';
import { useVendorName } from '../../../model/use-vendor-name';
import { InfoRow, ReviewSection } from '../review-modal-shell';
import { LocalizedRows } from './localized-rows';

/**
 * A promotion as one column (Figma 616:20275 body): names, owner (vendor +
 * branch, or the whole network), and the terms — a discount (percent or
 * fixed) or a buy-X-get-Y — with the validity window and product count.
 */
export function PromotionSnapshot({
  entity,
  request,
  t,
  verdictInfo,
}: {
  entity: ModerationEntity;
  request: ChangeRequest;
  t: Translate;
  verdictInfo?: ReactNode;
}) {
  const vendorName = useVendorName(request.merchant_id);
  const branchId = entityString(entity, 'restaurant_id') || requestBranchId(request);
  const branchName = useBranchName(branchId);
  const terms = promotionTerms(entity, t);
  const startsAt = entityString(entity, 'starts_at');
  const endsAt = entityString(entity, 'ends_at');
  const productCount = entityArrayLength(entity, 'product_ids');

  return (
    <>
      <ReviewSection title={t('promoCreate.nameSection', 'Наименование скидки')}>
        <LocalizedRows value={entityName(entity)} t={t} />
      </ReviewSection>

      <ReviewSection
        title={
          verdictInfo
            ? t('create.infoSection', 'Основная информация')
            : t('promoCreate.branchSection', 'Филиал продукта')
        }
      >
        <dl className="flex flex-col gap-2.5">
          <InfoRow label={t('create.vendor', 'Мерчант')} value={vendorName} />
          <InfoRow
            label={t('promoCreate.branch', 'Филиал')}
            value={branchId === '' ? t('review.allBranches', 'Вся сеть') : branchName}
          />
          {verdictInfo}
        </dl>
      </ReviewSection>

      <ReviewSection title={t('promoCreate.discountSection', 'Скидка')} divider={false}>
        <dl className="flex flex-col gap-2.5">
          <InfoRow
            label={t('promoCreate.discountType', 'Тип скидки')}
            value={terms.type}
          />
          <InfoRow label={terms.valueLabel} value={terms.value} numeric />
          <InfoRow
            label={t('promoCreate.startsAt', 'Время начала')}
            value={startsAt === '' ? '—' : formatDate(startsAt)}
            numeric
          />
          <InfoRow
            label={t('promoCreate.endsAt', 'Время окончания')}
            value={endsAt === '' ? '—' : formatDate(endsAt)}
            numeric
          />
          {productCount > 0 && (
            <InfoRow
              label={t('review.productCount', 'Товаров в акции')}
              value={String(productCount)}
              numeric
            />
          )}
        </dl>
      </ReviewSection>
    </>
  );
}

function promotionTerms(
  entity: ModerationEntity,
  t: Translate,
): { type: string; valueLabel: string; value: string } {
  const promoType = entityString(entity, 'promo_type').toUpperCase();
  const buy = entityNumber(entity, 'buy_quantity');
  const get = entityNumber(entity, 'get_quantity');
  if (promoType.includes('BUY') || (promoType === '' && (buy ?? get) !== undefined)) {
    return {
      type: t('review.buyXGetY', 'Купи X — получи Y'),
      valueLabel: t('review.buyGet', 'Купить / получить'),
      value: `${buy ?? '—'} → ${get ?? '—'}`,
    };
  }
  const discountType = entityString(entity, 'discount_type').toUpperCase();
  const value = entityNumber(entity, 'discount_value');
  if (discountType.includes('PERCENT')) {
    return {
      type: t('promoCreate.discountTypePercent', 'Процентная скидка'),
      valueLabel: t('promoCreate.discountValue', 'Процент скидки'),
      value: value === undefined ? '—' : `${value}%`,
    };
  }
  return {
    type: discountType.includes('FIXED')
      ? t('promoCreate.discountTypeFixed', 'Суммарная скидка')
      : '—',
    valueLabel: t('review.discountAmount', 'Сумма скидки'),
    value: value === undefined ? '—' : formatMoney(value),
  };
}
